import React, { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import {
  Autocomplete,
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { selectUser, selectUserRole } from "../../slices/user";
import api, { CreateUserPayload } from "../../api";
import { showError } from "../../utils/helpers";
import {
  isValidEmail,
  isValidPhone10Digits,
  sanitizePhoneInput,
} from "../../utils/userFormValidation";
import ExistingUserEmailModal from "../Modals/ExistingUserEmailModal";
import { AccessGroupsData } from "../../api/models/access-groups";
import { User } from "../../api/models/user-model";
import "../NewInvitationForm/new-invitation-form.css";

interface UnitInfo {
  key: string;
  label: string;
  id: number;
}

interface NewUserFormProps {
  roleToCreate: number;
  propertyIdProp?: number | null;
  propertyNameProp?: string;
  accessPointsProp?: number[];
  showForm: boolean;
  setShowForm: (showForm: boolean) => void;
  extraFunc: () => void;
  selectedUser?: User | null;
  setSelectedUser?: (selectedUser: User | null) => void;
  isMobile: boolean;
}

const NewUserForm = ({
  roleToCreate,
  propertyIdProp,
  propertyNameProp,
  accessPointsProp,
  showForm,
  setShowForm,
  extraFunc,
  selectedUser,
  setSelectedUser,
  isMobile,
}: NewUserFormProps) => {
  const labels = useAppSelector(selectLabels);
  const user = useAppSelector(selectUser);
  const userRole = useAppSelector(selectUserRole);
  const dispatch = useAppDispatch();

  const license = user?.license;
  const plan = license?.description.slice(-1).toLowerCase();

  const [countryCode, setCountryCode] = useState<"+52" | "+1">("+52");
  const [userName, setUserName] = useState<string>("");
  const [userPhone, setUserPhone] = useState<string>("");
  const [userEmail, setUserEmail] = useState<string>("");
  const [selectedAccessPoints, setSelectedAccessPoints] = useState<string[]>(
    [],
  );
  const [unitInputValue, setUnitInputValue] = useState<string>("");
  const [unit, setUnit] = useState<UnitInfo | null>(null);
  const [unitsArray, setUnitsArray] = useState<UnitInfo[]>([]);
  const [unitsLoaded, setUnitsLoaded] = useState<boolean>(false);
  const [userToCreate, setUserToCreate] = useState<"visitor" | "user">("user");
  const [userAccessGroups, setUserAccessGroups] = useState<
    AccessGroupsData[] | null
  >(null);
  const [finalRole, setFinalRole] = React.useState<number | null>(null);
  const [selectedRole, setSelectedRole] = React.useState<string>(
    `${roleToCreate}`,
  );
  const [accessPointsArray, setAccessPointsArray] = React.useState<
    { name: string; venue: string; value: string }[]
  >([]);
  const [showExistingEmailModal, setShowExistingEmailModal] =
    useState<boolean>(false);

  const roleToSelect = [
    { label: labels.user, value: "3" },
    { label: labels.security_guard, value: "4" },
    { label: labels.property_manager, value: "6" },
  ];

  const roleToSearch =
    roleToCreate === 3
      ? userToCreate === "visitor"
        ? `visitor_${plan}`
        : `host_${plan}`
      : roleToCreate === 5
        ? `secondary_user_${plan}`
        : roleToCreate === 6
          ? `property_admin_${plan}`
          : roleToCreate === 4
            ? `security_guard_${plan}`
            : "";

  React.useEffect(() => {
    const storedUser = localStorage.getItem("user");

    console.log("[NewUserForm][user] Usuario en Redux:", user);
    try {
      console.log(
        "[NewUserForm][user] Usuario guardado en localStorage:",
        storedUser ? JSON.parse(storedUser) : null,
      );
    } catch (error) {
      console.error(
        "[NewUserForm][user] No se pudo leer el usuario de localStorage:",
        error,
      );
    }
  }, [user]);

  const propertyId = user?.property?.id;
  const companyId = user?.company?.id;
  const unitForUsers = user?.unit?.id;
  const adminUnitName = `Administración - ${propertyNameProp}`;

  const defaultUnitsProps = {
    options: unitsArray ? unitsArray : [],
    getOptionLabel: (option: UnitInfo) => option.label,
  };

  const countryCodeArray = [
    {
      label: "MX +52",
      value: "+52",
    },
    {
      label: "US +1",
      value: "+1",
    },
  ];

  const accessPointsForUsers =
    user?.access_points.map((item) => item?.id) || [];

  const trimmedEmail = userEmail.trim();
  const hasValidEmail = Boolean(trimmedEmail) && isValidEmail(trimmedEmail);
  const hasValidPhone = isValidPhone10Digits(userPhone);
  const emailRequired =
    selectedRole !== "4" &&
    !(roleToCreate === 3 && userToCreate === "visitor");
  const emailIsValid = emailRequired
    ? hasValidEmail
    : !trimmedEmail || isValidEmail(trimmedEmail);

  const disabledCreateHostUser =
    !userName || !hasValidEmail || !hasValidPhone || !unit?.id;
  const disabledCreateSecUser = !userName || !hasValidEmail || !hasValidPhone;
  const disabledCreateAdmin = !userName || !hasValidEmail || !hasValidPhone;
  const disabledCreateSecGuard =
    !userName || !hasValidEmail || selectedAccessPoints.length <= 0;
  const disabledCreateVisitor = !userName || !hasValidPhone || !emailIsValid;
  const disabledCreate =
    roleToCreate === 3
      ? userToCreate === "visitor"
        ? disabledCreateVisitor
        : disabledCreateHostUser
      : roleToCreate === 5
        ? userToCreate === "visitor"
          ? disabledCreateVisitor
          : disabledCreateSecUser
        : roleToCreate === 4
          ? disabledCreateSecGuard
          : roleToCreate === 6
            ? disabledCreateAdmin
            : false;

  React.useEffect(() => {
    resetValues();
    if (!showForm && selectedUser && setSelectedUser) {
      setTimeout(() => {
        setSelectedUser(null);
      }, 300);
    }
  }, [selectedUser, setSelectedUser, showForm]);

  React.useEffect(() => {
    getPropertyAccessPoints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const getPropertyAccessPoints = async () => {
    if (!propertyId) return;
    try {
      const response = await api.accessPointsRequest(propertyId, 1, 100);
      if (response) {
        const currentAccessPoints = response.data.map((item) => ({
          name: item?.name,
          venue: item?.vanue_name,
          value: `${item?.id}`,
        }));
        setAccessPointsArray(currentAccessPoints);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    }
  };

  const resetValues = () => {
    setUserName("");
    setUserEmail("");
    setUserPhone("");
    setSelectedAccessPoints([]);
    setUnit(null);
  };

  React.useEffect(() => {
    if (selectedUser) {
      if (!selectedUser?.role?.name.startsWith("security_guard")) {
        const selectedUserPhone = selectedUser?.phone
          ? selectedUser?.phone.startsWith("+52")
            ? selectedUser?.phone.slice(3)
            : selectedUser?.phone.slice(2)
          : "";
        const selectedUserCountryCode = selectedUser?.phone
          ? selectedUser?.phone.startsWith("+52")
            ? "+52"
            : "+1"
          : "+52";
        setUserName(selectedUser?.name);
        setUserEmail(selectedUser?.email);
        setUserPhone(selectedUserPhone);
        setCountryCode(selectedUserCountryCode);
        if (selectedUser?.role?.name.startsWith("visitor")) {
          setUserToCreate("visitor");
        }
      } else {
        const selectedUserAccessPoints =
          selectedUser?.access_points.map((item) => `${item?.id}`) || [];
        setUserName(selectedUser?.name);
        setUserEmail(selectedUser?.email);
        setSelectedAccessPoints(selectedUserAccessPoints);
      }
    }
  }, [selectedUser]);

  const getRoleToCreate = React.useCallback(async () => {
    if (roleToSearch) {
      try {
        console.log("[NewUserForm][roles] Buscando rol:", {
          roleToSearch,
          licenseDescription: license?.description,
          plan,
        });
        const response = await api.userRolesRequest();
        console.table(
          response?.roles?.map(({ id, name }) => ({ id, name })) ?? [],
        );
        if (response) {
          const filteredUserRole =
            response?.roles.filter((item) => item?.name === roleToSearch)[0] ||
            null;
          console.log(
            "[NewUserForm][roles] Rol encontrado:",
            filteredUserRole,
          );
          if (filteredUserRole) {
            setFinalRole(filteredUserRole?.id);
          } else {
            console.error(
              `[NewUserForm][roles] STOP: no existe un rol con name="${roleToSearch}"`,
            );
          }
        }
      } catch (error: any) {
        console.error("[NewUserForm][roles] Error consultando roles:", error);
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    }
  }, [
    dispatch,
    labels.generic_error,
    license?.description,
    plan,
    roleToSearch,
  ]);

  React.useEffect(() => {
    getRoleToCreate();
  }, [getRoleToCreate]);

  const getAccessPointsByProperty = useCallback(async () => {
    if (propertyId) {
      try {
        let userRoleToCreate: number;
        const response = await api.accessGroupsRequest(
          propertyId,
          1,
          1000,
          false,
          "",
        );
        if (response && response?.data.length > 0) {
          if (roleToCreate === 3 || roleToCreate === 5) {
            if (userToCreate === "user") {
              userRoleToCreate = roleToCreate;
            } else {
              userRoleToCreate = 8;
            }
          } else {
            userRoleToCreate = roleToCreate;
          }
          const filteredAccessGroups = response?.data.filter((item) =>
            item?.role_permissions_users?.some(
              (roleItem) => roleItem?.id === userRoleToCreate,
            ),
          );
          if (filteredAccessGroups && filteredAccessGroups.length > 0) {
            setUserAccessGroups(filteredAccessGroups);
          }
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    }
  }, [dispatch, labels.generic_error, propertyId, roleToCreate, userToCreate]);

  useEffect(() => {
    getAccessPointsByProperty();
  }, [getAccessPointsByProperty]);

  const getUnitsByProperty = React.useCallback(async () => {
    if (userRole === "ciso") {
      if (propertyIdProp && roleToCreate === 6) {
        try {
          const response = await api.unitsRequest(
            Number(propertyIdProp),
            1,
            100,
          );
          if (response) {
            const array: UnitInfo[] = response.data.map((item, index) => {
              return {
                key: `${item?.id}-${index}`,
                label: item.name,
                id: item?.id,
              };
            });
            setUnitsArray(array);
            if (selectedUser && selectedUser?.unit) {
              const selectedUserUnit: UnitInfo = {
                key: `${selectedUser?.unit?.id}`,
                label: `${selectedUser?.unit?.name}`,
                id: selectedUser?.unit?.id,
              };
              setUnit(selectedUserUnit);
              setUnitInputValue(selectedUserUnit?.label);
            }
          }
        } catch (error: any) {
          console.error(error);
          const errorMessage = error?.response?.data?.error?.message;
          showError(
            dispatch,
            errorMessage ? errorMessage : labels.generic_error,
          );
        } finally {
          setUnitsLoaded(true);
        }
      }
    } else {
      if (propertyId && roleToCreate === 3) {
        try {
          const response = await api.unitsRequest(Number(propertyId), 1, 100);
          if (response) {
            const array: UnitInfo[] = response.data
              .filter((item) => !item?.is_admin)
              .map((item, index) => ({
                key: `${item?.id}-${index}`,
                label: item.name,
                id: item?.id,
              }));
            setUnitsArray(array);
            if (selectedUser && selectedUser?.unit) {
              const selectedUserUnit: UnitInfo = {
                key: `${selectedUser?.unit?.id}`,
                label: `${selectedUser?.unit?.name}`,
                id: selectedUser?.unit?.id,
              };
              setUnit(selectedUserUnit);
              setUnitInputValue(selectedUserUnit?.label);
            }
          }
        } catch (error: any) {
          console.error(error);
          const errorMessage = error?.response?.data?.error?.message;
          showError(
            dispatch,
            errorMessage ? errorMessage : labels.generic_error,
          );
        } finally {
          setUnitsLoaded(true);
        }
      }
    }
  }, [
    dispatch,
    labels.generic_error,
    propertyId,
    propertyIdProp,
    roleToCreate,
    selectedUser,
    userRole,
  ]);

  React.useEffect(() => {
    getUnitsByProperty();
  }, [getUnitsByProperty]);

  const phoneRequiredForForm = selectedUser
    ? !selectedUser.role?.name.startsWith("security_guard")
    : selectedRole !== "4";

  const emailRequiredForForm = selectedUser
    ? true
    : selectedRole !== "4" && !(roleToCreate === 3 && userToCreate === "visitor");

  const validateUserContactFields = (): boolean => {
    const email = userEmail.trim();

    if (emailRequiredForForm && !email) {
      showError(dispatch, labels.invalid_email);
      return false;
    }

    if (email && !isValidEmail(email)) {
      showError(dispatch, labels.invalid_email);
      return false;
    }

    if (phoneRequiredForForm && !isValidPhone10Digits(userPhone)) {
      showError(dispatch, labels.ten_digits_phone);
      return false;
    }

    return true;
  };

  const createUser = async () => {
    console.log("[NewUserForm][create] Click en crear:", {
      finalRole,
      roleToSearch,
      roleToCreate,
      selectedRole,
      licenseDescription: license?.description,
      userToCreate,
      userName,
      userEmail,
      userPhone,
      selectedAccessPoints,
      unit,
    });

    if (!finalRole) {
      console.error(
        "[NewUserForm][create] STOP: finalRole no fue encontrado; no se iniciará el registro",
      );
      return;
    }

    const contactFieldsAreValid = validateUserContactFields();
    console.log(
      "[NewUserForm][create] Validación de contacto:",
      contactFieldsAreValid,
    );
    if (!contactFieldsAreValid) return;

    if (finalRole) {
      const trimmedEmail = userEmail.trim().toLowerCase();
      if (trimmedEmail) {
        try {
          console.log(
            "[NewUserForm][create] Buscando usuario por correo:",
            trimmedEmail,
          );
          const existingUsers = await api.userByEmailRequest(trimmedEmail);
          console.log(
            "[NewUserForm][create] Resultado de búsqueda por correo:",
            existingUsers,
          );
          if (existingUsers?.length > 0) {
            console.warn(
              "[NewUserForm][create] STOP: el correo ya pertenece a otro usuario",
            );
            setShowExistingEmailModal(true);
            return;
          }
        } catch (error: any) {
          console.error(
            "[NewUserForm][create] STOP: error buscando el correo:",
            error,
          );
          console.error(error);
          const errorMessage = error?.response?.data?.error?.message;
          showError(
            dispatch,
            errorMessage ? errorMessage : labels.generic_error,
          );
          return;
        }
      }

      let payload: CreateUserPayload = {};
      let adminUnit: UnitInfo | null = null;
      if (roleToCreate === 3) {
        //PAYLOAD FOR HOST USER
        payload = {
          name: userName,
          username: userEmail,
          email:
            userToCreate === "user"
              ? userEmail
              : userEmail
                ? userEmail
                : `${userPhone}@passtrack.mx`,
          phone: countryCode + userPhone,
          unit: unit?.id,
          property: propertyId ? propertyId : null,
          company: companyId,
          password: "temporary",
          access_points: accessPointsForUsers,
          created_by_users: [Number(user?.id)],
          license: license?.id,
        };
      }
      if (roleToCreate === 5) {
        //PAYLOAD FOR SECONDARY USERS
        payload = {
          name: userName,
          username:
            userToCreate === "user"
              ? userEmail
              : userEmail
                ? userEmail
                : `${userPhone}@passtrack.mx`,
          email:
            userToCreate === "user"
              ? userEmail
              : userEmail
                ? userEmail
                : `${userPhone}@passtrack.mx`,
          phone: countryCode + userPhone,
          unit: unitForUsers,
          property: propertyId ? propertyId : null,
          company: companyId,
          password: "temporary",
          access_points: accessPointsForUsers,
          created_by_users: [Number(user?.id)],
          license: license?.id,
        };
      }
      if (roleToCreate === 4) {
        //PAYLOAD FOR SECURITY GUARDS
        payload = {
          name: userName,
          username: userEmail,
          email: userEmail,
          property: propertyId ? propertyId : null,
          company: companyId,
          password: "temporary",
          access_points: selectedAccessPoints.map((item) => Number(item)),
          created_by_users: [Number(user?.id)],
          license: license?.id,
        };
      }
      if (roleToCreate === 6) {
        //PAYLOAD FOR ADMINS
        if (userRole === "ciso") {
          adminUnit =
            unitsArray.filter(
              (unitItem) => unitItem?.label === adminUnitName,
            )[0] || null;
          payload = {
            name: userName,
            username: userEmail,
            email: userEmail,
            phone: countryCode + userPhone,
            property: propertyIdProp ? propertyIdProp : null,
            company: companyId,
            password: "temporary",
            access_points: accessPointsProp,
            created_by_users: [Number(user?.id)],
            license: license?.id,
          };
        } else {
          payload = {
            name: userName,
            username: userEmail,
            email: userEmail,
            phone: countryCode + userPhone,
            property: propertyId ? propertyId : null,
            company: companyId,
            password: "temporary",
            unit: unitForUsers,
            access_points: accessPointsForUsers,
            created_by_users: [Number(user?.id)],
            license: license?.id,
          };
        }
      }
      try {
        console.log("[NewUserForm][create] Payload preparado:", payload);
        if (userRole === "ciso") {
          if (adminUnit) {
            payload = {
              ...payload,
              unit: adminUnit?.id,
            };
            console.log(
              "[NewUserForm][create] Enviando custom-register (CISO con unidad):",
              payload,
            );
            const response = await api.createUser(payload);
            console.log(
              "[NewUserForm][create] Respuesta custom-register:",
              response,
            );
            if (response) {
              if (userAccessGroups && userAccessGroups?.length > 0) {
                for (const accessGroup of userAccessGroups) {
                  try {
                    await api.createUserAccessGroup({
                      data: {
                        user: response?.user?.id,
                        access_group: accessGroup?.id,
                        access: true,
                      },
                    });
                  } catch (err) {
                    console.error(
                      `Error adding user to access group ${accessGroup.id}`,
                      err,
                    );
                    showError(
                      dispatch,
                      `Error adding user to access group ${accessGroup.id}`,
                    );
                  }
                }
              }
              console.log(
                "[NewUserForm][create] Asignando rol:",
                finalRole,
              );
              const addRoleToUser = await api.addUserRole(
                response?.user?.id,
                finalRole,
              );
              console.log(
                "[NewUserForm][create] Respuesta al asignar rol:",
                addRoleToUser,
              );
              if (addRoleToUser) {
                console.log(
                  "[NewUserForm][create] Enviando correo de confirmación",
                );
                const sendEmail = await api.sendConfirmationEmail({
                  email: userEmail,
                  confirmation_token: String(response.confirmation_token),
                });
                console.log(
                  "[NewUserForm][create] Respuesta del correo:",
                  sendEmail,
                );
                if (sendEmail) {
                  setShowForm(false);
                  extraFunc();
                }
              }
            }
          } else {
            const newUnitPayload = {
              data: {
                name: `Administración - ${propertyNameProp}`,
                property: propertyIdProp,
                access_points: accessPointsProp,
              },
            };
            console.log(
              "[NewUserForm][create] Creando unidad administrativa:",
              newUnitPayload,
            );
            const newUnitResponse = await api.createUnitRequest(newUnitPayload);
            console.log(
              "[NewUserForm][create] Respuesta al crear unidad:",
              newUnitResponse,
            );
            if (newUnitResponse) {
              payload = {
                ...payload,
                unit: newUnitResponse?.id,
              };
              console.log(
                "[NewUserForm][create] Enviando custom-register (CISO con unidad nueva):",
                payload,
              );
              const response = await api.createUser(payload);
              console.log(
                "[NewUserForm][create] Respuesta custom-register:",
                response,
              );
              if (response) {
                if (userAccessGroups && userAccessGroups?.length > 0) {
                  for (const accessGroup of userAccessGroups) {
                    try {
                      await api.createUserAccessGroup({
                        data: {
                          user: response?.user?.id,
                          access_group: accessGroup?.id,
                          access: true,
                        },
                      });
                    } catch (err) {
                      console.error(
                        `Error adding user to access group ${accessGroup.id}`,
                        err,
                      );
                      showError(
                        dispatch,
                        `Error adding user to access group ${accessGroup.id}`,
                      );
                    }
                  }
                }
                console.log(
                  "[NewUserForm][create] Asignando rol:",
                  finalRole,
                );
                const addRoleToUser = await api.addUserRole(
                  response?.user?.id,
                  finalRole,
                );
                console.log(
                  "[NewUserForm][create] Respuesta al asignar rol:",
                  addRoleToUser,
                );
                if (addRoleToUser) {
                  console.log(
                    "[NewUserForm][create] Enviando correo de confirmación",
                  );
                  const sendEmail = await api.sendConfirmationEmail({
                    email: userEmail,
                    confirmation_token: String(response.confirmation_token),
                  });
                  console.log(
                    "[NewUserForm][create] Respuesta del correo:",
                    sendEmail,
                  );
                  if (sendEmail) {
                    setShowForm(false);
                    extraFunc();
                  }
                }
              }
            }
          }
        } else {
          console.log(
            "[NewUserForm][create] Enviando custom-register:",
            payload,
          );
          const response = await api.createUser(payload);
          console.log(
            "[NewUserForm][create] Respuesta custom-register:",
            response,
          );
          if (response) {
            let addRoleToUser;
            if (userToCreate === "user") {
              console.log(
                "[NewUserForm][create] Asignando rol:",
                finalRole,
              );
              addRoleToUser = await api.addUserRole(
                response?.user?.id,
                finalRole,
              );
            }
            if (userToCreate === "visitor") {
              console.log(
                "[NewUserForm][create] Asignando rol de visitante: 8",
              );
              addRoleToUser = await api.addUserRole(response?.user?.id, 8);
            }
            console.log(
              "[NewUserForm][create] Respuesta al asignar rol:",
              addRoleToUser,
            );
            if (userToCreate === "user") {
              if (addRoleToUser) {
                console.log(
                  "[NewUserForm][create] Enviando correo de confirmación",
                );
                const sendEmail = await api.sendConfirmationEmail({
                  email: userEmail,
                  confirmation_token: String(response.confirmation_token),
                });
                console.log(
                  "[NewUserForm][create] Respuesta del correo:",
                  sendEmail,
                );
                if (sendEmail) {
                  setShowForm(false);
                  resetValues();
                  extraFunc();
                }
              }
            } else {
              setShowForm(false);
              resetValues();
              extraFunc();
            }
          }
        }
      } catch (error: any) {
        console.error(
          "[NewUserForm][create] STOP: error durante el flujo:",
          error,
        );
        console.error(error);
        if (
          error?.response?.data?.error?.message ===
          "email must be a valid email"
        ) {
          showError(dispatch, labels.invalid_email);
        } else if (
          error?.response?.data?.error?.message ===
          "Email or Username are already taken"
        ) {
          showError(dispatch, labels.existed_user);
        } else {
          showError(dispatch, labels.generic_error);
        }
      }
    }
  };

  const updateUser = async () => {
    if (selectedUser) {
      if (!validateUserContactFields()) return;

      try {
        let payload: CreateUserPayload = {
          username: "",
          name: "",
          phone: "",
          email: "",
          unit: undefined,
        };
        if (selectedUser?.role?.name.startsWith("host")) {
          payload = {
            username: userEmail,
            name: userName,
            phone: `${countryCode}${userPhone}`,
            email: userEmail,
            unit: unit?.id,
          };
        }
        if (selectedUser?.role?.name.startsWith("secondary_user")) {
          payload = {
            username: userEmail,
            name: userName,
            phone: `${countryCode}${userPhone}`,
            email: userEmail,
          };
        }
        if (selectedUser?.role?.name.startsWith("security_guard")) {
          payload = {
            username: userEmail,
            name: userName,
            email: userEmail,
            access_points: selectedAccessPoints.map((item) => Number(item)),
          };
        }
        if (selectedUser?.role?.name.startsWith("property_admin")) {
          payload = {
            username: userEmail,
            name: userName,
            phone: `${countryCode}${userPhone}`,
            email: userEmail,
          };
        }
        const response = await api.updateUser(selectedUser?.id, payload);
        if (response) {
          setShowForm(false);
          resetValues();
          extraFunc();
        }
      } catch (error: any) {
        console.error(error);
        if (
          error?.response?.data?.error?.message ===
          "email must be a valid email"
        ) {
          showError(dispatch, labels.invalid_email);
        } else if (
          error?.response?.data?.error?.message ===
          "Email or Username are already taken"
        ) {
          showError(dispatch, labels.existed_user);
        } else if (
          error?.response?.data?.error?.message === "Email already taken"
        ) {
          showError(dispatch, labels.taken_email);
        } else {
          showError(dispatch, labels.generic_error);
        }
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === "visitor" || value === "user") {
      setUserToCreate(value);
    } else {
      console.error("Invalid value:", value); // Manejo de errores
    }
  };

  const showCreateUnitWarning =
    roleToCreate === 3 &&
    selectedRole === "3" &&
    !selectedUser &&
    userToCreate === "user" &&
    unitsLoaded &&
    unitsArray.length === 0;

  return (
    <div
      className={"border-box flex flex-dir-col gap-4"}
      style={{ width: isMobile ? "100%" : 660 }}
    >
      <ExistingUserEmailModal
        open={showExistingEmailModal}
        onClose={() => setShowExistingEmailModal(false)}
        email={userEmail.trim()}
      />
      {showCreateUnitWarning && (
        <Alert severity="warning" sx={{ width: "100%" }}>
          {labels.create_unit_before_users}
        </Alert>
      )}
      <div
        className={
          "w-100 border-box hidden flex flex-dir-col align-items-center gap-6 p-8"
        }
        style={{
          backgroundColor: "white",
          border: "1px #e0e0e0 solid",
          borderRadius: 6,
        }}
      >
        {isMobile && roleToCreate !== 5 && (
          <div className="w-100">
            <p className="t-nowrap body-text-xs mb-3">{labels.select_role} *</p>
            <Select
              fullWidth
              size="small"
              value={selectedRole}
              onChange={(event: SelectChangeEvent) => {
                setSelectedRole(event.target.value);
              }}
            >
              {roleToSelect.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}
        {(selectedRole === "3" || selectedRole === "5") && !selectedUser && (
          <div className="w-100">
            <FormControl>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={userToCreate}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="user"
                  control={<Radio />}
                  label={
                    selectedRole === "5"
                      ? `${labels.user} ${labels.from_unit} ${user?.unit?.name}`
                      : labels.user
                  }
                />
                <FormControlLabel
                  value="visitor"
                  control={<Radio />}
                  label={labels.recurrent_visitor}
                />
              </RadioGroup>
            </FormControl>
          </div>
        )}
        <div
          className={
            isMobile
              ? "w-100 border-box flex flex-dir-col align-items-center gap-6"
              : "w-100 border-box flex align-items-start gap-10"
          }
        >
          <div className={isMobile ? "w-100" : "w-50"}>
            <p className="t-nowrap body-text-xs mb-3">{labels.full_name} *</p>
            <TextField
              fullWidth
              placeholder={labels.enter_your_name}
              value={userName}
              size="small"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setUserName(event.target.value);
              }}
            />
          </div>
          {selectedRole === "4" && (
            <div className={isMobile ? "w-100" : "w-50"}>
              <p className="t-nowrap body-text-xs mb-3">{labels.email} *</p>
              <TextField
                fullWidth
                type="email"
                placeholder={"email@example.com"}
                value={userEmail}
                size="small"
                error={Boolean(trimmedEmail) && !isValidEmail(trimmedEmail)}
                helperText={
                  Boolean(trimmedEmail) && !isValidEmail(trimmedEmail)
                    ? labels.invalid_email
                    : undefined
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setUserEmail(event.target.value);
                }}
              />
            </div>
          )}
          {selectedRole !== "4" && (
            <div className={isMobile ? "w-100" : "w-50"}>
              <p className="t-nowrap body-text-xs mb-3">{labels.telephone} *</p>
              <div className="flex align-items-start justify-content-end gap-4">
                <FormControl style={{ width: 150 }}>
                  <Select
                    value={countryCode}
                    onChange={(event: SelectChangeEvent) =>
                      setCountryCode(event.target.value as "+52" | "+1")
                    }
                    size="small"
                  >
                    {countryCodeArray.map((item, index) => (
                      <MenuItem
                        key={`${index}-${item?.label}`}
                        value={item?.value}
                      >
                        {item?.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  type="tel"
                  inputProps={{ inputMode: "numeric", maxLength: 10 }}
                  placeholder={labels.ten_digit_number}
                  value={userPhone}
                  size="small"
                  error={Boolean(userPhone) && !hasValidPhone}
                  helperText={
                    Boolean(userPhone) && !hasValidPhone
                      ? labels.ten_digits_phone
                      : undefined
                  }
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUserPhone(sanitizePhoneInput(event.target.value));
                  }}
                />
              </div>
            </div>
          )}
        </div>
        <div className="w-100 border-box flex align-items-center gap-10">
          {selectedRole === "4" && (
            <>
              <div className={isMobile ? "w-100" : "w-50"}>
                <p className="t-nowrap body-text-xs mb-3">
                  {labels.access_points} *
                </p>
                <div className="flex flex-dir-col gap-4 access-points-container">
                  {accessPointsArray.map((item, index) => (
                    <div
                      key={`${index}-${item?.value}`}
                      className="w-100 flex align-items-center gap-2"
                      style={{ minWidth: 0 }}
                    >
                      <Checkbox
                        checked={selectedAccessPoints.includes(item?.value)}
                        onChange={() => {
                          const dupSelAccessPoints = [...selectedAccessPoints];
                          if (dupSelAccessPoints.includes(item?.value)) {
                            const indexValue = dupSelAccessPoints.indexOf(
                              item?.value,
                            );
                            dupSelAccessPoints.splice(indexValue, 1);
                          } else {
                            dupSelAccessPoints.push(item?.value);
                          }
                          setSelectedAccessPoints(dupSelAccessPoints);
                        }}
                      />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <p className="body-text-sm">{item?.name}</p>
                        <p className="body-text-xs title-color">
                          {item?.venue}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {!isMobile && <div className={isMobile ? "w-100" : "w-50"}></div>}
            </>
          )}
          {selectedRole !== "4" && (
            <div
              className={
                isMobile
                  ? "w-100 border-box flex flex-dir-col align-items-center gap-6"
                  : "w-100 border-box flex align-items-center gap-10"
              }
            >
              <div className={isMobile ? "w-100" : "w-50"}>
                <p className="t-nowrap body-text-xs mb-3">
                  {labels.email} {userToCreate === "user" && "*"}
                </p>
                <TextField
                  fullWidth
                  type="email"
                  placeholder={"email@example.com"}
                  value={userEmail}
                  size="small"
                  error={
                    Boolean(trimmedEmail) &&
                    !(emailRequired ? hasValidEmail : isValidEmail(trimmedEmail))
                  }
                  helperText={
                    Boolean(trimmedEmail) &&
                    !(emailRequired ? hasValidEmail : isValidEmail(trimmedEmail))
                      ? labels.invalid_email
                      : undefined
                  }
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUserEmail(event.target.value);
                  }}
                />
              </div>
              <div className={isMobile ? "w-100" : "w-50"}>
                {selectedRole === "3" && userToCreate === "user" && (
                  <>
                    <p className="t-nowrap body-text-xs mb-3">
                      {labels.unit} *
                    </p>

                    <Autocomplete
                      {...defaultUnitsProps}
                      autoComplete
                      value={unit}
                      onChange={(event: any, newValue: UnitInfo | null) => {
                        setUnit(newValue);
                      }}
                      inputValue={unitInputValue}
                      onInputChange={(event, newInputValue) => {
                        setUnitInputValue(newInputValue);
                      }}
                      id="Origen refactor"
                      size="small"
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <div
        className="w-100 border-box hidden flex flex-dir-col align-items-center gap-6 p-8"
        style={{
          backgroundColor: "white",
          border: "1px #e0e0e0 solid",
          borderRadius: 6,
        }}
      >
        <div className="w-100" style={{ textAlign: "center" }}>
          <p className="t-nowrap body-text-sm">* {labels.mandatory_fields}</p>
        </div>
        <div className="w-100" style={{ textAlign: "center" }}>
          <Button
            variant="contained"
            disableElevation
            disabled={disabledCreate}
            onClick={selectedUser ? updateUser : createUser}
          >
            {String(selectedRole) === "3" &&
              !selectedUser &&
              labels.create_user}
            {String(selectedRole) === "5" &&
              !selectedUser &&
              labels.create_user}
            {String(selectedRole) === "4" &&
              !selectedUser &&
              labels.create_sec_guard}
            {String(selectedRole) === "6" &&
              !selectedUser &&
              labels.create_admin}
            {selectedUser && labels.save_changes}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewUserForm;
